import _ from 'lodash';
import qs from 'qs';
import cx from 'classnames';
import Numeral from 'numeral';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ComplexTooltip, TooltipPlacement } from './tooltip';
import { TStreamInfo } from '../../../types/utilityTypes';
import { useSiteConfig } from '../../scripts/selectors/global';
import { useIdleCallback } from '../../scripts/hooks/useIdleCallback';

// Navigator Connection API (not in all TypeScript libs)
interface NetworkInformation {
  saveData: boolean;
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g';
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
}

// Constants for smart autoplay
const AUTOPLAY_DELAY_MS = 2500;
const INTERSECTION_THRESHOLD = 0.7;
const IDLE_CALLBACK_TIMEOUT_MS = 1500;

function isFastEnough(): boolean {
  const connection = typeof navigator !== 'undefined' ? navigator.connection : undefined;
  if (!connection) {
    return true; // Assume fast if API unavailable
  }
  if (connection.saveData) {
    return false;
  }
  return connection.effectiveType === '4g';
}

export function buildStreamThumbnail(urlTemplate: string, width: number, height: number) {
  return (urlTemplate || '').replace('{width}', width.toString()).replace('{height}', height.toString());
}

type StreamTypeMap = { [type in TStreamPreviewProps['type']]: string };

const streamUrlBases: StreamTypeMap = {
  douyu: 'https://open.douyu.com/tpl/h5/chain2/raiderio/',
  twitch: 'https://player.twitch.tv/'
};

const streamMuteArg: StreamTypeMap = {
  douyu: 'mute',
  twitch: 'muted'
};

const streamAutoplayArg: StreamTypeMap = {
  douyu: 'canplay',
  twitch: 'autoplay'
};

const streamReferralArg: StreamTypeMap = {
  douyu: 'refer',
  twitch: 'parent'
};

export function buildStreamUrl(
  type: TStreamPreviewProps['type'],
  autoplay: boolean,
  mute: boolean,
  streamIdentifier: string
) {
  let streamUrl = streamUrlBases[type];

  const queryParams = {
    [streamAutoplayArg[type]]: String(autoplay),
    [streamMuteArg[type]]: String(mute),
    [streamReferralArg[type]]: RIO_SITE.external_hostname
  };

  if (type === 'douyu') {
    queryParams.version = '2.0';
    streamUrl += streamIdentifier;
  } else if (type === 'twitch') {
    queryParams.channel = encodeURIComponent(streamIdentifier);
  } else {
    console.error(`Unsupported StreamPreview type ${type}`);
    return '';
  }

  return `${streamUrl}?${qs.stringify(queryParams)}`;
}

type TStreamPreviewProps = {
  className?: string;
  thumbnailUrl: string;
  width: string | number;
  height: string | number;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  streamerTitle?: any;
  viewerCount?: number;
  live?: boolean;
  makeLiveImmediately?: boolean;
  autoplay?: boolean;
  onClickThumbnail?: any;
  enableAudio?: boolean;
  hideTitle?: boolean;
  style?: any;
  type: 'twitch' | 'douyu';
  streamIdentifier: string;
};

type TStreamTooltipContentProps = {
  count: number;
  type: 'twitch' | 'douyu';
  stream: Pick<TStreamInfo, 'name' | 'viewer_count' | 'thumbnail_url'>;
  onClickThumbnail?: () => void;
};

export function StreamTooltipContent(props: TStreamTooltipContentProps) {
  return (
    <div className="slds-m-top--x-small slds-text-align--center">
      <div className="slds-m-bottom--xx-small">
        {props.count > 1 ? (
          <b className="text-white">{props.count} Members Streaming!</b>
        ) : (
          <b className="text-legendary">LIVE NOW</b>
        )}
      </div>
      <div>
        <StreamPreview
          onClickThumbnail={props.onClickThumbnail}
          type={props.type}
          thumbnailUrl={props.stream.thumbnail_url}
          width={142}
          height={80}
          viewerCount={props.stream.viewer_count}
          streamIdentifier={props.stream.name}
          className="slds-m-bottom--xx-small"
        />
      </div>
    </div>
  );
}

type TPossibleStreamTooltip = {
  stream: any;
  count?: number;
  hide?: boolean;
  placement?: TooltipPlacement;
  children: any;
  clickable?: boolean;
  onClickThumbnail?: () => void;
};

export function PossibleStreamTooltip(props: TPossibleStreamTooltip) {
  if (props.stream && !props.hide) {
    return (
      <ComplexTooltip
        tag="span"
        placement={props.placement}
        tooltip={
          <div style={{ width: 150, height: 100 }} className="slds-text-align--center slds-m-bottom--x-small">
            <StreamTooltipContent
              type="twitch"
              stream={props.stream}
              count={props.count!}
              onClickThumbnail={props.onClickThumbnail}
            />
          </div>
        }
        {...(props.clickable ? { clickableTooltip: true, mouseLeaveDelay: 0.1 } : {})}
      >
        {props.children}
      </ComplexTooltip>
    );
  }
  return props.children;
}

function StreamPreviewInner(props: TStreamPreviewProps) {
  const siteConfig = useSiteConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showIframe, setShowIframe] = useState(props.makeLiveImmediately || false);
  const visibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scheduleIdle, cancelIdle] = useIdleCallback(IDLE_CALLBACK_TIMEOUT_MS);

  const clearVisibilityTimer = useCallback(() => {
    if (visibilityTimerRef.current) {
      clearTimeout(visibilityTimerRef.current);
      visibilityTimerRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    clearVisibilityTimer();
    cancelIdle();
  }, [clearVisibilityTimer, cancelIdle]);

  const handleClick = useCallback(() => {
    clearAllTimers();
    setShowIframe(true);
    props.onClickThumbnail?.();
  }, [clearAllTimers, props.onClickThumbnail]);

  useEffect(() => {
    if (props.makeLiveImmediately && !showIframe) {
      clearAllTimers();
      setShowIframe(true);
    }
  }, [clearAllTimers, props.makeLiveImmediately, showIframe]);

  // Smart autoplay logic with IntersectionObserver
  useEffect(() => {
    const container = containerRef.current;
    const shouldSkip =
      !props.live ||
      showIframe ||
      props.makeLiveImmediately ||
      typeof IntersectionObserver === 'undefined' ||
      !container;

    if (shouldSkip) {
      return;
    }

    const startVisibilityTimer = () => {
      if (visibilityTimerRef.current) {
        return; // Already timing
      }

      visibilityTimerRef.current = setTimeout(() => {
        visibilityTimerRef.current = null;
        if (!document.hidden && isFastEnough()) {
          scheduleIdle(() => setShowIframe(true));
        }
      }, AUTOPLAY_DELAY_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearAllTimers();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((e) => e.intersectionRatio >= INTERSECTION_THRESHOLD);
        if (isVisible && !document.hidden) {
          startVisibilityTimer();
        } else {
          clearAllTimers();
        }
      },
      { threshold: [0, INTERSECTION_THRESHOLD] }
    );

    observer.observe(container);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearAllTimers();
    };
  }, [props.live, showIframe, props.makeLiveImmediately, clearAllTimers, scheduleIdle]);

  if (!props.streamIdentifier) {
    return null;
  }

  const thumbnailUrl =
    buildStreamThumbnail(
      props.thumbnailUrl,
      2 * (props.thumbnailWidth ? props.thumbnailWidth : Number(props.width)),
      2 * (props.thumbnailHeight ? props.thumbnailHeight : Number(props.height))
    ) + `?${siteConfig.serverTimeMs}`;

  const title = props.streamerTitle || props.streamIdentifier;
  const altText = `${props.streamIdentifier} ${_.capitalize(props.type)} Stream`;
  const showViewerCount = props.viewerCount !== undefined && props.viewerCount > 0;

  // Live mode: show iframe once loaded
  if (props.live && showIframe) {
    const isAutoplaying = props.autoplay ?? true;
    const streamUrl = buildStreamUrl(props.type, isAutoplaying, !props.enableAudio, String(props.streamIdentifier));
    if (!streamUrl) {
      return null;
    }
    return (
      <iframe
        src={streamUrl}
        width={props.width}
        height={props.height}
        frameBorder="0"
        scrolling="auto"
        allowFullScreen={true}
        className={`stream-live ${props.className || ''}`}
        style={props.style || undefined}
      />
    );
  }

  // Thumbnail/facade view (non-live mode, or live mode before autoplay triggers)
  const isLiveFacade = props.live && !showIframe;
  return (
    <div
      ref={isLiveFacade ? containerRef : undefined}
      className={cx('stream-preview', props.className)}
      style={{
        backgroundImage: `url(${thumbnailUrl})`,
        ...(isLiveFacade && { aspectRatio: '16 / 9', width: props.width, height: props.height })
      }}
      onClick={!isLiveFacade ? props.onClickThumbnail : undefined}
    >
      <img src={thumbnailUrl} width={props.width} height={props.height} loading="lazy" alt={altText} />
      {isLiveFacade ? (
        <button
          className="stream-overlay slds-align--absolute-center"
          aria-label={`Play ${altText}`}
          onClick={handleClick}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', width: '100%', height: '100%' }}
        >
          <i className="fa fa-play-circle text-white" />
        </button>
      ) : (
        <div className="stream-overlay slds-align--absolute-center">
          <i className="fa fa-play-circle text-white" />
        </div>
      )}
      {showViewerCount && <div className="stream-viewer-count"> {Numeral(props.viewerCount).format('0,0')} </div>}
      {!props.hideTitle && title && <div className="stream-streamer-name">{title}</div>}
    </div>
  );
}

export function StreamPreview(props: TStreamPreviewProps) {
  return <StreamPreviewInner key={`${props.type}:${props.streamIdentifier}`} {...props} />;
}
